import { createContext, useContext, useState, ReactNode, useMemo } from 'react'
import { updateMedicalReview } from 'services/exam'
import Storage from 'storage/auth'
import { IExamResultResponse } from 'types/exam/types'

type AgreementStatus = 'agree' | 'disagree' | null

interface DiagnosisAgreementContextType {
  agreements: Record<string, AgreementStatus>
  setAgreement: (diagnosisId: string, status: AgreementStatus) => void
  allDiagnosesReviewed: (diagnosisIds: string[]) => boolean
  isLoading: Record<string, boolean>
  /** Resolve `true` quando a discordância foi gravada (ou é só local). */
  handleAgreement: (diagnostic: any) => Promise<boolean>
  initializeAgreements: (results: IExamResultResponse[]) => void
}

const DiagnosisAgreementContext = createContext<
  DiagnosisAgreementContextType | undefined
>(undefined)

export const DiagnosisAgreementProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const [agreements, setAgreements] = useState<Record<string, AgreementStatus>>(
    {}
  )
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({})

  const initializeAgreements = (results: IExamResultResponse[]) => {
    const initialAgreements: Record<string, AgreementStatus> = {}

    results.forEach(result => {
      if (result.professional_review === 'Zero') {
        initialAgreements[result.result_type] = 'disagree'
      } else {
        initialAgreements[result.result_type] = null
      }
    })

    setAgreements(initialAgreements)
  }

  const setAgreement = (diagnosisId: string, status: AgreementStatus) => {
    setAgreements(prev => ({
      ...prev,
      [diagnosisId]: status
    }))
  }

  const setLoading = (diagnosisId: string, loading: boolean) => {
    setIsLoading(prev => ({
      ...prev,
      [diagnosisId]: loading
    }))
  }

  const updateAgreement = (diagnostic: any) => {
    setAgreement(
      diagnostic.key,
      agreements[diagnostic.key] === 'disagree' ? null : 'disagree'
    )
  }

  const handleRequestAgreement = async (diagnostic: any): Promise<boolean> => {
    setLoading(diagnostic.key, true)
    try {
      const uid = Storage.user.get()?.professional_id || ''
      const newStatus = agreements[diagnostic.key] === 'disagree' ? '1' : '0'

      const response = await updateMedicalReview({
        id_exam_result: diagnostic.id_exam_result,
        id_professional: uid,
        professional_review: newStatus
      })

      if (response.success) {
        updateAgreement(diagnostic)
      }
      return response.success
    } catch (error) {
      console.error('Error in handleRequestAgreement:', error)
      throw error
    } finally {
      setLoading(diagnostic.key, false)
    }
  }

  const handleAgreement = async (diagnostic: any): Promise<boolean> => {
    if (diagnostic.id_exam_result) {
      return handleRequestAgreement(diagnostic)
    }
    updateAgreement(diagnostic)
    return true
  }

  const allDiagnosesReviewed = (diagnosisIds: string[]): boolean => {
    return diagnosisIds.every(
      id => agreements[id] !== null && agreements[id] !== undefined
    )
  }

  const contextValue = useMemo(
    () => ({
      agreements,
      setAgreement,
      allDiagnosesReviewed,
      isLoading,
      handleAgreement,
      initializeAgreements
    }),
    [agreements, isLoading]
  )

  return (
    <DiagnosisAgreementContext.Provider value={contextValue}>
      {children}
    </DiagnosisAgreementContext.Provider>
  )
}

export const useDiagnosisAgreement = () => {
  const context = useContext(DiagnosisAgreementContext)
  if (!context) {
    throw new Error(
      'useDiagnosisAgreement must be used within DiagnosisAgreementProvider'
    )
  }
  return context
}
